import { isEmpty, clone, camelCase, isNil, isEqual, find } from 'lodash';
import { APM_OBJTYPE } from '@/constants/objdefine';
import { EMPTY_TEXT } from '@/utils/string';
import { convertValuByUnit, convertThousandSeparatorFormat, convertMillisecondsToSeconds } from '@/utils/number';

const UNIT = {
  count: 'count',
  error: 'count',
  elapsed: 'sec',
  cpu: 'sec',
  mem: 'Byte',
  occuredCount: 'count',
  navCount: 'count',
  ajaxCount: 'count',
  jserrorCount: 'count',
  time: 'sec',
};

export default {
  props: {
    objtype: String,
    active: Boolean,
    params: Object,
    columnName: {
      type: String,
      default: null,
    },
  },
  computed: {
    objtypeName() {
      return APM_OBJTYPE[this.objtype];
    },
    requestParams() {
      if (isEmpty(this.params)) {
        return null;
      }
      return Object.assign(clone(this.params), { requestTypeText: camelCase(this.objtype) });
    },
  },
  watch: {
    active: {
      immediate: true,
      handler(active) {
        if (active) {
          this.loadData();
        }
      },
    },
    /**
     * beforeParams가 null일 경우는 active watcher에서 loadData 수행.
     * 전 파라메터와 현재 파라메터가 다를 경우에만 로드
     * @param  {[type]} newParams    [description]
     * @param  {[type]} beforeParams [description]
     * @return {[type]}              [description]
     * @author jda
     */
    requestParams(newParams, beforeParams) {
      if (isEmpty(beforeParams)) {
        return;
      }
      if (this.active && !isEqual(newParams, beforeParams)) {
        this.loadData();
      }
    },
    columnName: {
      immediate: true,
      handler(columnName) {
        if (!isEmpty(columnName)) {
          this.columns.unshift({ prop: 'idText', width: 500, headerAlign: this.headerAlign, label: `${columnName}`, sortable: true });
        } else {
          this.columns.unshift({ prop: 'idText', width: 400, headerAlign: this.headerAlign, label: `${this.objtypeName}명`, sortable: true, scopedSlot: 'name-slot' });
        }
      },
    },
  },
  methods: {
    /**
     * 1000의 자리 숫자 , 표현
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertThousandSeparatorFormat(row, column, value) {
      if (isNil(value)) {
        return EMPTY_TEXT;
      }
      return convertThousandSeparatorFormat(value);
    },
    /**
     * milliseconds -> seconds 단위 변환
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertMillisecondsValue(row, column, value) {
      return convertMillisecondsToSeconds(value);
    },
    /**
     * Byte 단위 변환
     * @param  {[type]} row    [description]
     * @param  {[type]} column [description]
     * @param  {[type]} value  [description]
     * @return {[type]}        [description]
     * @author jda
     */
    convertByteValue(row, column, value) {
      return convertValuByUnit(value, 'Byte', true, true, true);
    },
    /**
     * 테이블 항목 선택 시 APM 트리에서 선택한다.
     * @param  {[type]} record [description]
     * @return {[type]}        [description]
     * @author jda
     */
    selectApmTree(record) {
      const objtype = this.objtype;
      let requestType = null;
      if (objtype.indexOf('TIER') > -1) {
        requestType = 'tier';
      } else {
        requestType = camelCase(objtype);
      }

      this.getCurrentNodeTreeKey(this.$store.state.apm.commonTreeData, requestType, record.id);
    },
    /**
     * 선택한 항목의 treeKey를 찾아 store에 저장한다.
     * @param  {[type]} root        [description]
     * @param  {[type]} requestType [description]
     * @param  {[type]} id          [description]
     * @return {[type]}             [description]
     * @author jda
     */
    getCurrentNodeTreeKey(root, requestType, id) {
      if (!isEmpty(root)) {
        root.forEach((child) => {
          if (isNil(child[`${requestType}Id`]) && !isEmpty(child.children)) {
            this.getCurrentNodeTreeKey(child.children, requestType, id);
          } else if (child[`${requestType}Id`] === id) {
            this.$store.dispatch('setSelectedTreeKey', child.treeKey);
          }
        });
      }
    },
    /**
     * 테이블 헤더 선택 시 하위에 차트를 그려준다. (티어, 비즈니스, 어플리케이션, 키트랜잭션 요약 탭에서만 가능)
     * @param  {[type]} column [description]
     * @return {[type]}        [description]
     * @author jda
     */
    onClickHeader(column) {
      if (isEmpty(column.property) || column.property === 'idText' || column.property === 'typeCount') {
        return;
      }
      const table = this.$refs.sortedTable;
      // 이미 정렬된 데이터를 가져오기 위해 테이블의 데이터를 직접 조회
      this.$nextTick(() => {
        const sortedData = table.getTable().data;
        const series = [];
        const parentInfo = column.level === 2 ? this.getParentInfo(table.columns, column.property) : null;
        sortedData.forEach((data) => {
          const seriesData = data.rawData.map(({ datetime: x, [column.property]: y }) => ({ x, y }));
          series.push({
            data: seriesData,
            name: data.idText,
            unit: column.level === 2 ? UNIT[parentInfo.prop] : UNIT[column.property],
          });
        });
        const title = isEmpty(parentInfo) ? column.label : `${column.label} ${parentInfo.label}`;
        this.$emit('loadChartData', { title, series });
      });
    },
    /**
     * childColumn의 헤더를 선택 할 경우 부모 컬럼 정보를 가져온다.
     * @param  {[type]} columns  [description]
     * @param  {[type]} property [description]
     * @return {[type]}          [description]
     * @author jda
     */
    getParentInfo(columns, property) {
      let parentInfo;
      columns.forEach((parent) => {
        if (property.indexOf(parent.prop) > -1) {
          parentInfo = parent;
        }
      });
      return parentInfo;
    },
    /**
     * 탭 전환시 마다 요약화면 하위 차트를 재 로드한다.
     * @return {[type]} [description]
     * @author jda
     */
    initLoadChart() {
      const selectedType = this.requestParams.selectedObjectTypeText;
      if (selectedType !== 'tier' && selectedType !== 'biz') {
        return;
      }
      this.$nextTick(() => {
        if (isEmpty(this.$refs.sortedTable)) {
          return;
        }
        const table = this.$refs.sortedTable.getTable();
        const column = find(table.columns, ['property', this.defaultChartColumn]);
        this.onClickHeader(column);
      });
    },
  },
};



// WEBPACK FOOTER //
// ./src/mixins/apm/summary.js